import { motion } from "framer-motion";
import { useVisibleMotion } from "@/hooks/use-visible-motion";

/** Orbiting ring with a dot */
export const OrbitSystem = ({
  size = 200, ringColor = "border-blue-glow/15", dotColor = "bg-orange-cta",
  duration = 20, reverse = false, className = "",
}: { size?: number; ringColor?: string; dotColor?: string; duration?: number; reverse?: boolean; className?: string }) => {
  const { ref, active } = useVisibleMotion<HTMLDivElement>();
  return (
  <div ref={ref} className={`absolute pointer-events-none ${className}`} style={{ width: size, height: size }}>
    <motion.div className={`w-full h-full rounded-full border ${ringColor}`}
      animate={active ? { rotate: reverse ? -360 : 360 } : { rotate: 0 }}
      transition={{ duration, repeat: Infinity, ease: "linear" }}>
      <div className={`absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 rounded-full ${dotColor} shadow-lg`} />
    </motion.div>
  </div>
  );
};

/** Double orbit rings */
export const DoubleOrbit = ({ size = 300, className = "" }: { size?: number; className?: string }) => (
  <div className={`absolute pointer-events-none ${className}`} style={{ width: size, height: size }}>
    <motion.div className="absolute inset-0 rounded-full border border-blue-glow/10"
      animate={{ rotate: 360 }} transition={{ duration: 25, repeat: Infinity, ease: "linear" }}>
      <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-blue-glow shadow-lg shadow-blue-glow/50" />
    </motion.div>
    <motion.div className="absolute rounded-full border border-purple-accent/10"
      style={{ inset: size * 0.12 }}
      animate={{ rotate: -360 }} transition={{ duration: 18, repeat: Infinity, ease: "linear" }}>
      <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-purple-accent shadow-lg shadow-purple-accent/50" />
    </motion.div>
  </div>
);

/** Glowing pulsing orb */
export const GlowOrb = ({
  size = 120, color = "bg-blue-glow/10", blur = "blur-2xl", duration = 4, className = "",
}: { size?: number; color?: string; blur?: string; duration?: number; className?: string }) => (
  <motion.div className={`absolute rounded-full ${color} ${blur} pointer-events-none ${className}`}
    style={{ width: size, height: size }}
    animate={{ scale: [1, 1.3, 1], opacity: [0.3, 0.6, 0.3] }}
    transition={{ duration, repeat: Infinity, ease: "easeInOut" }} />
);

/** Floating geometric shape */
export const FloatingShape = ({
  size = 40, color = "border-blue-glow/20", duration = 8, delay = 0,
  className = "", shape = "diamond",
}: { size?: number; color?: string; duration?: number; delay?: number; className?: string; shape?: "diamond" | "square" | "circle" }) => {
  const rotation = shape === "diamond" ? "rotate-45" : "";
  const rounded = shape === "circle" ? "rounded-full" : shape === "square" ? "rounded-lg" : "rounded-sm";
  return (
    <motion.div className={`absolute border ${color} ${rotation} ${rounded} pointer-events-none ${className}`}
      style={{ width: size, height: size }}
      animate={{ y: [0, -25, 0], x: [0, 10, 0], rotate: shape === "diamond" ? [45, 90, 45] : [0, 180, 360], opacity: [0.2, 0.5, 0.2] }}
      transition={{ duration, delay, repeat: Infinity, ease: "easeInOut" }} />
  );
};

/** Connected nodes */
export const ConnectedNodes = ({
  className = "", length = 100, angle = 0, color = "bg-blue-glow",
  lineColor = "bg-blue-glow/20", duration = 6,
}: { className?: string; length?: number; angle?: number; color?: string; lineColor?: string; duration?: number }) => (
  <motion.div className={`absolute pointer-events-none ${className}`}
    style={{ width: length, transform: `rotate(${angle}deg)` }}
    animate={{ opacity: [0.3, 0.7, 0.3] }}
    transition={{ duration, repeat: Infinity, ease: "easeInOut" }}>
    <div className="relative flex items-center justify-between">
      <motion.div className={`w-2.5 h-2.5 rounded-full ${color} shadow-sm`}
        animate={{ scale: [1, 1.4, 1] }}
        transition={{ duration: duration * 0.7, repeat: Infinity, ease: "easeInOut" }} />
      <div className={`absolute left-3 right-3 h-px ${lineColor}`} />
      <motion.div className={`w-2 h-2 rounded-full ${color}/60`}
        animate={{ scale: [1.2, 0.8, 1.2] }}
        transition={{ duration: duration * 0.5, repeat: Infinity, ease: "easeInOut", delay: 1 }} />
    </div>
  </motion.div>
);

/** Data stream */
export const DataStream = ({
  className = "", vertical = false, length = 200, duration = 3,
}: { className?: string; vertical?: boolean; length?: number; duration?: number }) => (
  <div className={`absolute overflow-hidden pointer-events-none ${className}`}
    style={{ width: vertical ? 2 : length, height: vertical ? length : 2 }}>
    <motion.div className="bg-gradient-to-r from-transparent via-blue-glow/40 to-transparent"
      style={{ width: vertical ? 2 : 40, height: vertical ? 40 : 2 }}
      animate={vertical ? { y: [-40, length] } : { x: [-40, length] }}
      transition={{ duration, repeat: Infinity, ease: "linear" }} />
  </div>
);

/** Pulsing radar circle */
export const RadarPulse = ({
  size = 150, color = "border-blue-glow/20", className = "",
}: { size?: number; color?: string; className?: string }) => (
  <div className={`absolute pointer-events-none ${className}`} style={{ width: size, height: size }}>
    {[0, 1, 2].map((i) => (
      <motion.div key={i} className={`absolute inset-0 rounded-full border ${color}`}
        animate={{ scale: [0.5, 1.5], opacity: [0.6, 0] }}
        transition={{ duration: 3, repeat: Infinity, ease: "easeOut", delay: i * 1 }} />
    ))}
    <div className="absolute inset-0 flex items-center justify-center">
      <div className="w-2 h-2 rounded-full bg-blue-glow shadow-lg shadow-blue-glow/50" />
    </div>
  </div>
);

/** Hexagonal grid pattern */
export const HexGrid = ({ className = "", size = 300, opacity = 0.04 }: { className?: string; size?: number; opacity?: number }) => (
  <motion.svg className={`absolute pointer-events-none ${className}`} width={size} height={size} viewBox="0 0 200 200"
    animate={{ opacity: [opacity * 0.5, opacity, opacity * 0.5], rotate: [0, 3, 0] }}
    transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}>
    {[
      "M60,10 L90,10 L105,36 L90,62 L60,62 L45,36Z",
      "M110,10 L140,10 L155,36 L140,62 L110,62 L95,36Z",
      "M85,48 L115,48 L130,74 L115,100 L85,100 L70,74Z",
      "M35,48 L65,48 L80,74 L65,100 L35,100 L20,74Z",
      "M135,48 L165,48 L180,74 L165,100 L135,100 L120,74Z",
      "M60,86 L90,86 L105,112 L90,138 L60,138 L45,112Z",
      "M110,86 L140,86 L155,112 L140,138 L110,138 L95,112Z",
      "M85,124 L115,124 L130,150 L115,176 L85,176 L70,150Z",
    ].map((d, i) => (
      <motion.path key={i} d={d} fill="none" stroke="currentColor" strokeWidth="0.5"
        className="text-blue-glow"
        animate={{ opacity: [0.2, 0.8, 0.2] }}
        transition={{ duration: 4, delay: i * 0.5, repeat: Infinity, ease: "easeInOut" }} />
    ))}
  </motion.svg>
);

/** Network mesh with animated connections */
export const NetworkMesh = ({ className = "", width = 400, height = 200 }: { className?: string; width?: number; height?: number }) => {
  const nodes = [
    { x: 20, y: 30 }, { x: 80, y: 15 }, { x: 150, y: 40 }, { x: 220, y: 20 },
    { x: 290, y: 45 }, { x: 350, y: 25 }, { x: 50, y: 80 }, { x: 120, y: 100 },
    { x: 200, y: 85 }, { x: 270, y: 110 }, { x: 340, y: 90 }, { x: 100, y: 150 },
    { x: 180, y: 140 }, { x: 260, y: 160 }, { x: 330, y: 145 },
  ];
  const edges = [
    [0, 1], [1, 2], [2, 3], [3, 4], [4, 5], [0, 6], [6, 7], [7, 8], [8, 9], [9, 10],
    [1, 7], [2, 8], [3, 9], [4, 10], [7, 11], [11, 12], [12, 13], [13, 14], [8, 12], [9, 13],
  ];
  return (
    <svg className={`absolute pointer-events-none ${className}`} width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
      {edges.map(([a, b], i) => (
        <motion.line key={i} x1={nodes[a].x} y1={nodes[a].y} x2={nodes[b].x} y2={nodes[b].y}
          stroke="hsl(200 100% 55%)" strokeWidth="0.5"
          animate={{ opacity: [0.05, 0.2, 0.05] }}
          transition={{ duration: 3, delay: i * 0.2, repeat: Infinity, ease: "easeInOut" }} />
      ))}
      {nodes.map((n, i) => (
        <motion.circle key={i} cx={n.x} cy={n.y} r="2" fill="hsl(200 100% 55%)"
          animate={{ opacity: [0.15, 0.6, 0.15], r: [1.5, 2.5, 1.5] }}
          transition={{ duration: 4, delay: i * 0.3, repeat: Infinity, ease: "easeInOut" }} />
      ))}
    </svg>
  );
};

/** Pulsing beacon */
export const PulseBeacon = ({
  className = "", color = "bg-blue-glow", size = 8,
}: { className?: string; color?: string; size?: number }) => (
  <div className={`absolute pointer-events-none ${className}`}>
    <motion.div className={`rounded-full ${color}`} style={{ width: size, height: size }}
      animate={{ scale: [1, 1.5, 1], opacity: [0.8, 0.3, 0.8] }}
      transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }} />
    <motion.div className={`absolute inset-0 rounded-full ${color}/30`}
      style={{ width: size * 3, height: size * 3, top: -size, left: -size }}
      animate={{ scale: [0.5, 1.5], opacity: [0.4, 0] }}
      transition={{ duration: 2, repeat: Infinity, ease: "easeOut" }} />
  </div>
);

/** Dashboard mockup card – glassmorphism floating card */
export const DashboardCard = ({
  className = "", children, delay = 0,
}: { className?: string; children: React.ReactNode; delay?: number }) => {
  const { ref, active } = useVisibleMotion<HTMLDivElement>();
  return (
  <motion.div ref={ref} className={`absolute pointer-events-none z-30 ${className}`}
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay, duration: 0.8 }}>
    <motion.div className="bg-card/[0.08] backdrop-blur-md border border-primary-foreground/[0.08] rounded-xl shadow-2xl"
      animate={active ? { y: [0, -6, 0] } : { y: 0 }}
      transition={{ duration: 5, delay, repeat: Infinity, ease: "easeInOut" }}>
      {children}
    </motion.div>
  </motion.div>
  );
};

/** Animated bar chart */
export const MiniBarChart = ({ className = "" }: { className?: string }) => {
  const bars = [40, 65, 45, 80, 55, 70, 90, 60];
  return (
    <div className={`flex items-end gap-1 h-10 ${className}`}>
      {bars.map((h, i) => (
        <motion.div key={i} className="w-1.5 rounded-full bg-blue-glow/60"
          initial={{ height: 0 }}
          animate={{ height: `${h}%` }}
          transition={{ delay: 0.8 + i * 0.1, duration: 0.6, ease: "easeOut" }} />
      ))}
    </div>
  );
};

/** Animated line sparkline */
export const MiniSparkline = ({ className = "" }: { className?: string }) => (
  <svg className={`${className}`} viewBox="0 0 100 30" width="100" height="30">
    <motion.path d="M0,25 Q10,20 20,18 T40,12 T60,15 T80,8 T100,5"
      fill="none" stroke="hsl(200 100% 55%)" strokeWidth="1.5"
      initial={{ pathLength: 0 }} animate={{ pathLength: 1 }}
      transition={{ delay: 1, duration: 1.5, ease: "easeOut" }} />
    <motion.path d="M0,25 Q10,20 20,18 T40,12 T60,15 T80,8 T100,5 V30 H0Z"
      fill="hsl(200 100% 55% / 0.08)" stroke="none"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      transition={{ delay: 2, duration: 0.8 }} />
  </svg>
);
